// Purchases use an explicit display zone; the API accepts only a UTC instant.
export const PURCHASE_TIME_ZONE = 'Europe/Rome';
export const PURCHASE_TIME_ZONES = ['CET', 'CEST'];
// Keep older saved purchase reviews readable without offering these zones in the form.
const LEGACY_TIME_ZONES = [
  'Europe/Rome',
  'UTC',
  'Asia/Jakarta',
  'Asia/Makassar',
  'Asia/Jayapura',
];
const fixedTimeZones = { CET: 'Etc/GMT-1', CEST: 'Etc/GMT-2' };
const intlTimeZone = timeZone => fixedTimeZones[timeZone] || timeZone;

const partsInZone = (date, timeZone) => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: intlTimeZone(timeZone),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  return Object.fromEntries(parts.map(part => [part.type, part.value]));
};

export const paymentWallTime = (
  date = new Date(),
  timeZone = PURCHASE_TIME_ZONE
) => {
  const p = partsInZone(date, timeZone);
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
};

export const timeZoneLabel = (timeZone, date = new Date()) => {
  if (PURCHASE_TIME_ZONES.includes(timeZone)) return timeZone;
  const indonesia = {
    'Asia/Jakarta': 'WIB',
    'Asia/Makassar': 'WITA',
    'Asia/Jayapura': 'WIT',
  };
  return (
    indonesia[timeZone] ||
    new Intl.DateTimeFormat('en-GB', {
      timeZone,
      timeZoneName: 'short',
    })
      .formatToParts(date)
      .find(part => part.type === 'timeZoneName').value
  );
};

export const offsetLabel = minutes => {
  const absolute = Math.abs(minutes);
  return `UTC${minutes < 0 ? '−' : '+'}${String(
    Math.floor(absolute / 60)
  ).padStart(2, '0')}:${String(absolute % 60).padStart(2, '0')}`;
};

export const paymentTimeOptions = (wallTime, timeZone) => {
  if (
    !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(wallTime || '') ||
    ![...PURCHASE_TIME_ZONES, ...LEGACY_TIME_ZONES].includes(timeZone)
  )
    return [];
  const naive = new Date(`${wallTime}:00Z`);
  if (
    !Number.isFinite(naive.getTime()) ||
    naive.toISOString().slice(0, 16) !== wallTime
  )
    return [];
  // Inspect offsets on both sides of a clock change, then round-trip each candidate.
  // A spring gap has no candidates; an autumn overlap has two and requires a choice.
  const offsets = new Set(
    [-36, -12, 0, 12, 36].map(hours => {
      const instant = new Date(naive.getTime() + hours * 3600000);
      return (
        (Date.parse(`${paymentWallTime(instant, timeZone)}:00Z`) -
          instant.getTime()) /
        60000
      );
    })
  );
  return [...offsets]
    .map(offset => ({
      offset,
      date: new Date(naive.getTime() - offset * 60000),
    }))
    .filter(({ date }) => paymentWallTime(date, timeZone) === wallTime)
    .sort((a, b) => a.date - b.date)
    .map(({ date, offset }) => ({
      iso: date.toISOString(),
      offset,
      label: `${timeZoneLabel(timeZone, date)} (${offsetLabel(offset)})`,
    }));
};

export const paymentZoneLabel = (wallTime, timeZone, offset = '') => {
  const options = paymentTimeOptions(wallTime, timeZone);
  if (options.length > 1 && offset === '')
    return options
      .map(option => timeZoneLabel(timeZone, new Date(option.iso)))
      .join('/');
  const selected =
    options.find(option => option.offset === Number(offset)) || options[0];
  const day = new Date(`${wallTime?.slice(0, 10)}T12:00:00Z`);
  return timeZoneLabel(
    timeZone,
    selected
      ? new Date(selected.iso)
      : Number.isFinite(day.getTime())
      ? day
      : new Date()
  );
};

export const paymentTimeToUtc = (wallTime, timeZone, offset = '') => {
  const options = paymentTimeOptions(wallTime, timeZone);
  if (!options.length)
    throw new Error(
      'Enter a valid payment date and time in the selected timezone. This time may not exist during a daylight-saving change.'
    );
  if (options.length === 1) return options[0].iso;
  const selected =
    offset === ''
      ? undefined
      : options.find(option => option.offset === Number(offset));
  if (!selected)
    throw new Error(
      'This time occurs twice when daylight saving ends. Choose the payment timezone offset.'
    );
  return selected.iso;
};

export const formatPurchaseTime = (value, timeZone = PURCHASE_TIME_ZONE) => {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return '—';
  const text = new Intl.DateTimeFormat('en-GB', {
    timeZone: intlTimeZone(timeZone),
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(date);
  return `${text.replace(' Sept ', ' Sep ')} ${timeZoneLabel(timeZone, date)}`;
};
